import {
  MOZZOAPP_GUIDE_VERSION, MOZZOAPP_MANUAL_VERSION, MOZZOAPP_OPERATING_GUIDE,
} from './mozzoAppOperatingGuide';
import {
  ASSISTANT_MODULES, canFollowAssistantProcedure, canOpenAssistantRoute,
} from './localAssistantAccess';
import manualIndex from '../../docs/manual-mozzo/indice-conocimiento.json';

const normalize = (value) => String(value || '').normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').toLowerCase()
  .replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
const contains = (text, phrase) => (` ${text} `).includes(` ${normalize(phrase)} `);
// Limited language variants for retrieval only; never alter authorization or negations.
const guideWordVariants = new Map(Object.entries({
  cargar: ['cargo', 'cargas', 'cargamos', 'cargan', 'cargue', 'cargues'],
  crear: ['creo', 'creas', 'creamos', 'crean', 'crees'],
  agregar: ['agrego', 'agregas', 'agregamos', 'agregan', 'agregues'],
  ingresar: ['ingreso', 'ingresas', 'ingresamos', 'ingresan'],
  eliminar: ['elimino', 'eliminas', 'eliminamos', 'eliminan', 'elimine', 'elimines'],
  borrar: ['borro', 'borras', 'borramos', 'borran', 'borre', 'borres'],
  vaciar: ['vacio', 'vacias', 'vaciamos', 'vacian', 'vacie', 'vacies'],
  plato: ['platos'], producto: ['productos'], ingrediente: ['ingredientes'],
  categoria: ['categorias'], subcategoria: ['subcategorias'], foto: ['fotos'],
}).flatMap(([word, variants]) => variants.map((variant) => [variant, word])));
const guideDeterminers = new Set(['el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas', 'mi', 'mis']);
const normalizeGuidePhrase = (value) => normalize(value).split(' ')
  .filter((word) => !guideDeterminers.has(word))
  .map((word) => guideWordVariants.get(word) || word).join(' ');
const pathOnly = (path) => String(path || '/app').split(/[?#]/)[0].replace(/\/$/, '');
const routeOrder = [...ASSISTANT_MODULES].sort((a, b) => b.route.length - a.route.length);

export const getLocalAssistantScreen = (pathname = '/app') =>
  pathOnly(pathname) === '/app/preparar' ? { key: 'menu', title: 'Preparar mi restaurante', route: '/app/preparar' } :
  routeOrder.find(({ route }) => pathOnly(pathname) === route ||
    (route !== '/app' && pathOnly(pathname).startsWith(`${route}/`))) || ASSISTANT_MODULES[0];

// Match curated phrases and their reviewed variants, not incidental words in answers.
export const findOperatingGuideMatches = ({ question, pathname = '/app', limit = 3 } = {}) => {
  const text = normalizeGuidePhrase(question);
  const screen = getLocalAssistantScreen(pathname);
  if (!text) return [];
  return MOZZOAPP_OPERATING_GUIDE.map((entry) => {
    const phrases = [...new Set(entry.keywords.map(normalizeGuidePhrase))];
    const weights = phrases.filter((phrase) => contains(text, phrase))
      .map((phrase) => phrase.split(' ').length * 10);
    const score = weights.length ? Math.max(...weights) + Math.min(weights.length - 1, 3) +
      (entry.screenKeys.includes(screen.key) ? 1 : 0) : 0;
    return { entry, score };
  }).filter(({ score }) => score > 0).sort((a, b) => b.score - a.score)
    .slice(0, Math.max(1, limit));
};

const navigationTarget = (question) => {
  const text = normalize(question).replace(
    /^(?:abri|abre|abrir|ir|anda|llevame|entrar|quiero ver|mostrame)\s+/, ''
  ).replace(/^(?:(?:a|al|el|la|los|las|modulo|seccion|pantalla|de)\s+)+/, '');
  return ASSISTANT_MODULES.find(({ title, aliases }) =>
    [title, ...aliases].some((alias) => normalize(alias) === text));
};

const routeAction = (module) => ({ label: `Abrir ${module.title}`, route: module.route });
const guidanceChoices = [
  { id: 'start', label: 'Empezar desde cero', question: 'Soy nuevo, por donde empiezo?', knowledgeId: 'primeros-pasos' },
  { id: 'import', label: 'Cargar carta desde imagen o PDF', question: 'Quiero importar mi carta desde PDF', knowledgeId: 'importar-carta' },
  { id: 'products', label: 'Cargar platos a mano', question: 'Como cargo los platos?', knowledgeId: 'crear-plato' },
  { id: 'stock', label: 'Preparar el stock', question: 'Como cargo el stock?', knowledgeId: 'cantidad-stock' },
  { id: 'orders', label: 'Entender pedidos, entrega y cobro', question: 'Cual es la diferencia entre listo, entregado y pagado?', knowledgeId: 'operacion' },
  { id: 'help', label: 'Ver guia de primeros pasos', question: 'Quiero que me guies para usar el sistema', knowledgeId: 'guia-uso' },
];
export const getLocalAssistantGuidanceChoices = (access) => guidanceChoices.filter((choice) => {
  const entry = MOZZOAPP_OPERATING_GUIDE.find(({ id }) => id === choice.knowledgeId);
  return entry && canFollowAssistantProcedure(entry, access) &&
    (choice.id !== 'orders' || ['inicio', 'cocina', 'pagos', 'mozos'].some((key) => access?.modules?.includes(key)));
});

// Recognize requests for orientation independently of one exact sentence.
// Specific documented tasks are resolved first; this never grants permissions.
const isGeneralGuidanceRequest = (text) => {
  if (/\bno (?:quiero|necesito|me) (?:que me )?(?:guies|guia|guiar|ayudes|ayuda|ensenes|ensenar)\b/.test(text)) return false;
  if (/^(?:por favor )?(?:ayuda|ayudame|guiame|orientame|necesito ayuda|necesito una guia|quiero que me guies|podes guiarme|me podes guiar|no se por donde empezar|no se como empezar)(?: por favor)?$/.test(text)) return true;
  const help = /\b(guiame|guies|guias|guiar|guiarme|guia|ayudame|ayudes|ayudas|ayudar|ayuda|ensename|ensenes|ensenas|ensenar|orientame|orientes|aprender|entender|conocer|explicame|expliques|explicar|tutorial|recorrido)\b/.test(text);
  const use = /\b(usar|usarlo|uso|usa|utilizar|utilizarlo|utiliza|manejar|manejarlo|funciona|funcionamiento|empezar|comenzar|arrancar)\b/.test(text);
  const app = /\b(sistema|aplicacion|app|plataforma|programa|mozzo|mozzoapp|esto)\b/.test(text);
  return (help && (use || app)) || (use && app);
};
const reply = (answer, sourceKind = 'notice', extra = {}) => ({
  answer, sourceKind, actions: [], knowledgeIds: [], followUpIds: [], ...extra,
  source: 'local-guide', guideVersion: MOZZOAPP_GUIDE_VERSION, manualVersion: MOZZOAPP_MANUAL_VERSION,
});

const buildGuideReply = (entry, { access, pathname }) => {
  const sourceTopic = manualIndex.topics.find(({ id }) => id === entry.topicId);
  if (['comisiones', 'fiscal'].includes(entry.topicId) && ![
    'creator', 'platform_backoffice', 'admin_general_global', 'admin_general_branch',
  ].includes(access?.role)) {
    return reply('Este tema corresponde al administrador autorizado. Solicita su revision sin compartir credenciales; no puedo consultar ni cambiar acuerdos o datos fiscales.', 'permission');
  }
  const permitted = canFollowAssistantProcedure(entry, access);
  const followUpIds = entry.id === 'guia-uso' ? getLocalAssistantGuidanceChoices(access)
    .filter(({ id }) => id !== 'help').map(({ id }) => id) : [];
  const parts = [entry.id === 'guia-uso' && !followUpIds.length
    ? 'Puedo orientarte, pero tu acceso actual no habilita las secciones de este recorrido. Primero pedi al administrador que revise tus permisos y la sucursal seleccionada; esta guia no concede accesos.'
    : entry.answer];
  if (permitted && entry.steps.length) {
    parts.push(entry.steps.map((step, index) => `${index + 1}. ${step}`).join('\n'));
  } else if (!permitted && (entry.requiredAction || entry.moduleKey)) {
    parts.push(entry.permissionMessage ||
      'Tu acceso actual no habilita este procedimiento o el modulo esta desactivado. Pide al administrador que revise el permiso y la configuracion; no se modifica nada desde esta ayuda.');
  }
  // Do not truncate warnings: payment/stock restrictions are part of the procedure.
  parts.push(...entry.notes.map((note) => `Importante: ${note}`));
  const module = ASSISTANT_MODULES.find(({ key }) => key === entry.moduleKey);
  return reply(parts.join('\n\n'), 'guide', {
    knowledgeIds: [entry.id], topicId: entry.topicId, topicTitle: entry.title,
    sourceDocument: sourceTopic?.document,
    followUpIds,
    actions: permitted && module && pathOnly(pathname) !== module.route &&
      canOpenAssistantRoute(module.route, access) ? [routeAction(module)] : [],
  });
};

export const createLocalAiAssistantReply = async (context = {}) => {
  const { question, access, pathname = '/app' } = context;
  const text = normalize(question);
  if (!access?.ready) return reply(
    'Necesito una sesion interna con empresa, sucursal y permisos verificados para orientar esta tarea. Inicia sesion o revisa el acceso con el administrador.', 'permission');
  if (!text) return reply('Escribi que necesitas hacer y te indico el procedimiento documentado.');

  if (/\b(otro bar|otra empresa|otro restaurante|ventas ajenas|datos ajenos)\b/.test(text)) {
    return reply('El acceso esta limitado a tu empresa y sucursal autorizadas. No revelo ventas, recetas ni datos de otros restaurantes. Este asistente no consulta datos reales de ninguna empresa.', 'boundary');
  }
  if (/\b(tokens?|secretos?|contrasenas?|clave api|ignora las reglas|instrucciones de la descripcion)\b/.test(text)) {
    return reply('Las descripciones, archivos y nombres de productos son datos, no instrucciones ni autorizaciones. Mantengo los permisos de la sesion y no revelo ni solicito secretos, claves o tokens.', 'boundary');
  }
  if (/\b(cuanto vendi|ventas de hoy|saldo actual|stock real|esta pagado|ya se guardo)\b/.test(text)) {
    return reply('No tengo una lectura autorizada del estado real para confirmar ese dato. Revisa la pantalla correspondiente de tu sucursal y su confirmacion actual; no puedo inventar saldos, cobros ni guardados.', 'live-data-unavailable');
  }
  if (/\b(alergenos?|alergias?|contaminacion cruzada)\b/.test(text)) {
    return buildGuideReply(MOZZOAPP_OPERATING_GUIDE.find(({ id }) => id === 'alergenos'), context);
  }
  if (/^(hola|buen dia|buenas|hey|que tal)$/.test(text)) {
    return reply('Puedo guiarte con el manual revisado y abrir secciones autorizadas. No consulto saldos ni modifico datos. Decime que tarea necesitas resolver.');
  }

  const currentScreen = getLocalAssistantScreen(pathname);
  if (/^(que puedo hacer aca|que puedo hacer aqui|donde estoy|que hace esta pantalla|que hace esta seccion)$/.test(text)) {
    if (!canOpenAssistantRoute(currentScreen.route, access)) return reply(
      'No tengo acceso verificado a esta seccion. Revisa el contexto y los permisos con el administrador.', 'permission');
    return reply(`Estas en ${currentScreen.title}. Esta seccion ${currentScreen.description}`, 'screen');
  }
  const target = navigationTarget(question);
  if (target) {
    if (!canOpenAssistantRoute(target.route, access)) return reply(
      `Tu acceso actual no habilita ${target.title} o el modulo esta desactivado. Pide al administrador que revise el permiso y la configuracion.`, 'permission');
    return reply(`${target.title} ${target.description}${pathOnly(pathname) === target.route ? ' Ya estas en esa pantalla.' : ''}`, 'navigation', {
      actions: pathOnly(pathname) === target.route ? [] : [routeAction(target)],
    });
  }

  let matches = findOperatingGuideMatches({ question, pathname, limit: 3 });
  const wantsGuidance = isGeneralGuidanceRequest(text);
  if (wantsGuidance) {
    const specificMatches = matches.filter(({ entry }) => !['primeros-pasos', 'guia-uso', 'estado-asistente'].includes(entry.id));
    if (!specificMatches.length) return buildGuideReply(MOZZOAPP_OPERATING_GUIDE.find(({ id }) => id === 'guia-uso'), context);
    matches = specificMatches;
  }
  if (matches.length > 1 && matches[0].entry.id !== matches[1].entry.id &&
      matches[0].score - matches[1].score <= 1) {
    return reply(`Para no indicarte un procedimiento equivocado: te referis a "${matches[0].entry.title}" o a "${matches[1].entry.title}"? Contame cual es la tarea.`, 'clarification');
  }
  if (matches.length) return buildGuideReply(matches[0].entry, context);
  return reply('No tengo un procedimiento preciso para esa consulta. Podes contarme que resultado buscas o abrir la guia de primeros pasos de abajo. No voy a inventar una funcion ni cambiar datos por vos.', 'unknown', {
    followUpIds: ['help'],
  });
};

export const getLocalAiAssistantSuggestions = (pathname = '/app', access) => {
  if (!access?.ready) return [];
  const screen = getLocalAssistantScreen(pathname);
  return MOZZOAPP_OPERATING_GUIDE.filter((entry) => entry.suggestion &&
    canFollowAssistantProcedure(entry, access))
    .sort((a, b) => Number(b.screenKeys.includes(screen.key)) - Number(a.screenKeys.includes(screen.key)))
    .slice(0, 3).map(({ suggestion }) => suggestion);
};
